import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Settings, Cog, Flame, Wrench, Drill, Hammer, Zap, Shield, Network, CheckCircle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Settings, Cog, Flame, Wrench, Drill, Hammer, Zap, Shield, Network
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0 }
};

export const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Wrench;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="relative pt-28 pb-20 md:py-20 px-6 bg-[#121212] border-b-4 border-[#FF9900]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Desktop Back Button */}
            <Link
              to="/"
              data-testid="back-to-home"
              className="hidden md:inline-flex items-center gap-2 text-[#FF9900] hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium uppercase">Back to Home</span>
            </Link>

            {/* Mobile Back Button: Home Icon in Top Right Corner */}
            <Link
              to="/"
              data-testid="back-to-home-mobile"
              className="md:hidden fixed top-3 right-3 z-50 bg-[#1E1E1E] border border-[#27272A] p-3 text-[#FF9900] hover:bg-[#FF9900] hover:text-black transition-all duration-300 rounded-sm shadow-md flex items-center justify-center"
              title="Back to Home"
            >
              <Home className="w-6 h-6" />
            </Link>

            <h1 className="text-5xl sm:text-6xl font-bold text-[#FF9900] mb-6">OUR SERVICES</h1>
            <p className="text-gray-300 text-xl max-w-3xl leading-relaxed">
              Expert welding, fabrication, and maintenance services for the agriculture and oil industries.
              No job is too big or too small.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-16 h-16 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-4">Loading services...</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="space-y-6"
            >
              {services.map((service, index) => {
                const Icon = getIcon(service.icon);
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    data-testid={`service-${service.id}`}
                    className="bg-[#121212] border border-[#27272A] hover:border-[#FF9900] transition-all duration-300 p-8 flex flex-col md:flex-row gap-6 items-start group"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-[#FF9900]/10 flex items-center justify-center border-2 border-[#FF9900] group-hover:bg-[#FF9900] transition-colors duration-300">
                        <Icon className="w-8 h-8 text-[#FF9900] group-hover:text-black transition-colors duration-300" />
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-6 w-full">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-[#FF9900]/50 font-bold text-sm">0{index + 1}</span>
                          <h3 className="text-3xl font-bold text-[#F3F4F6] uppercase">{service.name}</h3>
                        </div>
                        <p className="text-gray-400 text-lg leading-relaxed">{service.description}</p>
                      </div>
                      <Link 
                        to={`/#contact?description=${encodeURIComponent('Inquiry about ' + service.name + ' Service: ')}`}
                        className="flex-shrink-0 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-white transition-all duration-300 w-full md:w-auto text-center"
                      >
                        Request Quote
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {!loading && services.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">No services available at the moment.</p>
            </div>
          )}
        </div>
      </div>

      <div className="py-16 px-6 bg-[#121212]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12"
          >
            <div>
              <h2 className="text-3xl font-bold text-[#FF9900] mb-6 uppercase">Why Choose Us</h2>
              <div className="space-y-4">
                {[
                  'Qualified professionals with decades of experience',
                  'High quality standards and on-time deliveries',
                  '20,000 square feet of modern shop facilities',
                  'Serving agriculture and oil industries since 1975',
                  'Competitive pricing and honest estimates',
                  'Shop and portable services available'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF9900] flex-shrink-0 mt-1" />
                    <p className="text-gray-300 text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1E1E1E] p-8 border-l-4 border-[#FF9900]">
              <h3 className="text-2xl font-bold text-[#F3F4F6] mb-4 uppercase">Request A Quote</h3>
              <p className="text-gray-400 mb-6 text-lg">
                Call us today with your project information or send us an inquiry online. You will be provided with an honest estimate and quick turnaround times.
              </p>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">PHONE</p>
                  <p className="text-2xl font-bold text-[#FF9900]">(403) 223-2620</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">EMAIL</p>
                  <p className="text-lg text-gray-300">george@eastendiron.com</p>
                </div>
              </div>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 mt-6"
              >
                Go to Contact Form
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;